import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Layout } from '@/components/layout'
import { useWorkerStatus } from '@/api/system'
import { RefreshCw, Users, ArrowLeft } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { nl } from 'date-fns/locale'

export function AdminWorkersPage() {
  const { t } = useTranslation()
  const { data: workerData, isLoading: workerLoading, refetch: refetchWorkers } = useWorkerStatus()

  const getWorkerStateColor = (state: string) => {
    switch (state.toLowerCase()) {
      case 'idle':
        return 'bg-green-100 text-green-800'
      case 'busy':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <Layout>
      {/* Header */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => (window.location.href = '/admin')}
                className="mb-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('admin.terugNaarOverzicht')}
              </Button>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                {t('admin.werkers')}
              </h1>
              <p className="text-muted-foreground mt-2">{t('admin.werkersDetails')}</p>
            </div>
            <Button onClick={() => refetchWorkers()} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('admin.vernieuwen')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8 space-y-6">
        {/* Worker Stats Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="h-5 w-5" />
              <span>{t('admin.werkersamenvatting')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {workerData?.totalWorkers || 0}
                </div>
                <p className="text-sm text-muted-foreground">{t('admin.totaalWerkers')}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {workerData?.workers.filter(w => w.state === 'idle').length || 0}
                </div>
                <p className="text-sm text-muted-foreground">{t('admin.inactieveWerkers')}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {workerData?.workers.filter(w => w.state === 'busy').length || 0}
                </div>
                <p className="text-sm text-muted-foreground">{t('admin.bezigeWerkers')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Worker Details */}
        <div>
          <h2 className="text-xl font-semibold mb-4">{t('admin.werkersDetails')}</h2>
          <div className="grid gap-4">
            {workerLoading ? (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                  {t('admin.werkersLaden')}
                </CardContent>
              </Card>
            ) : workerData?.workers.length === 0 ? (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <Users className="h-6 w-6 text-muted-foreground mr-2" />
                  {t('admin.geenActieveWerkers')}
                </CardContent>
              </Card>
            ) : (
              workerData?.workers.map(worker => (
                <Card key={worker.name}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{worker.name}</CardTitle>
                      <Badge className={getWorkerStateColor(worker.state)}>{worker.state}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">{t('admin.huidigeTaak')}</p>
                        <p className="font-medium">{worker.currentJob || t('admin.geen')}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t('admin.geslaagdeTaken')}</p>
                        <p className="font-medium text-green-600">{worker.successfulJobs}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t('admin.mislukteTaken')}</p>
                        <p className="font-medium text-red-600">{worker.failedJobs}</p>
                      </div>
                    </div>
                    {worker.birthDate && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm text-muted-foreground">
                          {t('admin.gestart')}{' '}
                          {formatDistanceToNow(new Date(worker.birthDate), {
                            addSuffix: true,
                            locale: nl,
                          })}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}
